/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import type { CancelTokenSource } from 'axios';

import { C11nEnv } from '../interpreter/c11n-env';
import { invokeRestApi, getCancelTokenSource, isRequestCanceled } from '../rest-client';
import { fetchViewResources, updateViewResources } from '../utils/view-resources';

const POST_CUSTOM_MESSAGES = 'postCustomFeedMessages';
const POST_FEED_MESSAGES = 'postFeedMessages';
const DELETE_FEED_MESSAGE = 'deleteFeedMessage';
const GET_FEED_MESSAGES = 'getFeedMessages';
const LIKE_FEED_MESSAGES = 'likeFeedMessages';
const UNLIKE_FEED_MESSAGES = 'unlikeFeedMessages';
const GET_MENTION_SUGGESTIONS = 'getMentionSuggestions';
const GET_TAG_SUGGESTIONS = 'getTagSuggestions';
const GET_FEED_MESSAGE_LIKES = 'getFeedMessageLikes';
const EDIT_FEED_MESSAGE = 'editFeedMessage';
const GET_MENTION_TYPES = 'getMentionTypes';
const GET_VIEW_FOR_POSTTYPE = 'getViewRuleByMessageType';

const headers = { 'Content-Type': 'application/json' };

type CustomMessageRequest = {
  context: string;
  content: object;
  pageInstructions?: object[];
};

type CustomMessageResponse = {
  status: number;
  data: {
    data: {
      pyIsDeleteEnabled: boolean;
      pyPostType: string;
      postedTime: string;
      postedByUser: {
        name: string;
        ID: string;
      };
      ID: string;
      classID: string;
      messageViewReference: string;
      PostMessage: object;
      pyCommentContext: string;
    };
    uiResources?: object;
  };
};

type Recipient = {
  pyUserName: string;
  pxObjClass?: string;
  pyUserIdentifier: string;
  pyPosition?: string;
};

type FeedFilter = {
  id: string;
  label: string;
  on: boolean;
  disabled: boolean;
};

type Message = {
  ID: string;
  pzInsKey?: string;
  mentions?: MentionItem[];
  pxResults?: Message[];
  [key: string]: any;
};

type MentionItem = {
  mentionsID: string;
  mentionsType: string;
  [key: string]: any;
};

/**
 * @description Use the APIs in the FeedUtils class to handle the feeds of a case.
 */
class FeedUtils {
  public mentionsTagsCancelTokenSource: CancelTokenSource[];
  public mentions: MentionItem[];

  constructor() {
    this.mentionsTagsCancelTokenSource = [];
    this.mentions = [];
  }

  /**
   * Obtains the feeds for a given context.
   * @function
   * @static
   * @example <caption>In this example, the API obtains the feed of a context whose feedID is pyDashboardFeed.</caption>
   * PCore.getFeedUtils().getFeeds('DATA-PORTAL $EngPMF', 'pyDashboardFeed','class',[{id: 'All', label: 'All', on: false, disabled: false}],[], getPConnect(), true)
   * .then(feedResponse => {
   *    // feedResponse array
   * }).catch(err => {
   *   // errors
   * });
   * @example <caption>In this example, the API obtains the feed of a context whose feedID is pyCaseFeed.</caption>
   * PCore.getFeedUtils().getFeeds('PEGAPROJMGMT-WORK TASK-100', 'pyCaseFeed','class',[{id: 'All', label: 'All', on: false, disabled: false}],[], getPConnect(), true)
   * .then(feedResponse => {
   *    // feedResponse array
   * }).catch(err => {
   *   // errors
   * });
   *
   * @param pulseContext                     The name of the application context or case context for which the feed must be fetched.
   * @param feedID                           The ID of the feed that must be fetched.
   *                                                         FeedID of the Pulse widget, when configured on the Home page, is known as "pyDashboardFeed"
   *                                                         and when used within a case view, it is known as "pyCaseFeed."
   * @param feedClass                        The class associated with the feed to be fetched.
   * @param feedFilters                      The list of conditions through which the feed to be fetched is filtered.
   *                                                         It is an array comprising objects, with each object containing four distinct properties.
   *                                                         id and label values for the filter options are available in pyDashboardFeed and pyCaseFeed rules.
   *                                                         Extra filter options if needed can be configured in the same rules.
   * @param     {string}    feedFilters.id                   The unique identifier for the feed source
   * @param     {string}    feedFilters.label                The title of the feed source
   * @param     {boolean}   feedFilters.disabled             This can be used to deactivate the filter (with a default value of false)
   * @param     {boolean}   feedFilters.on                   This provides information regarding whether the filter is currently selected.(with a default value of false i.e., not selected)
   * @param fetchFeedsCancelTokenSource      The list of API requests for fetching the feed.
   * @param pConnectObj                          The PConnect object of the component from where the API is being called.
   * @param isLoadMore                       The flag that determines if the next set of feeds should be loaded.
   *                                                         The default value is false. Set isLoadMore to true if you want to load the next set of feeds.
   * @returns A Promise that resolves to an object.
   */
  getFeeds = (
    pulseContext: string,
    feedID: string,
    feedClass: string,
    feedFilters: FeedFilter[],
    fetchFeedsCancelTokenSource: CancelTokenSource[],
    pConnectObj: C11nEnv,
    isLoadMore: boolean
    // eslint-disable-next-line sonarjs/cognitive-complexity
  ) => {
    const l11nService = pConnectObj.getLocalizationService();
    if (!isLoadMore) this.mentions = [];
    let filterBy = '';
    if (feedFilters && Array.isArray(feedFilters) && feedFilters?.length > 0) {
      feedFilters.forEach((feedFilter) => {
        if (feedFilter.on) filterBy = `${filterBy}${feedFilter.id},`;
      });
      /* istanbul ignore if */
      if (filterBy === '') filterBy = 'ClearFilters';
    }
    let olderThan = '';
    if (isLoadMore) {
      olderThan = pConnectObj.getValue('pulse.pageLastEntry');
    }

    const queryPayload = {
      filterForContext: pulseContext,
      feedID,
      feedClass,
      filterBy,
      pageSize: 10,
      olderThan
    };

    for (let i = 0; i < fetchFeedsCancelTokenSource.length; i += 1) {
      fetchFeedsCancelTokenSource[i].cancel();
    }
    const newCancelTokenSource = getCancelTokenSource();
    fetchFeedsCancelTokenSource.push(newCancelTokenSource);
    return invokeRestApi(
      GET_FEED_MESSAGES,
      { queryPayload, cancelTokenSource: newCancelTokenSource },
      pConnectObj.getContextName()
    )
      .then(async (response: any) => {
        if (response.status === 200 && response.data) {
          fetchFeedsCancelTokenSource.pop();
          const respMessageIDs: any[] = [];
          const respMessages: { [key: string]: any } = {};
          let isCustomViewConfigured = false;
          let postTypes = [];

          if (response?.data) {
            postTypes =
              Array.isArray(response.data.data.MessageTypes) && response.data.data.MessageTypes.length
                ? response.data.data.MessageTypes
                : [];

            if (response?.data?.data?.FeedList) {
              const feedItemList = response.data.data.FeedList?.pxResults;

              if (Array.isArray(feedItemList) && feedItemList?.length > 0) {
                feedItemList.forEach((message, index) => {
                  message.ID = message.pzInsKey !== '' ? message.pzInsKey : `message${index}`;
                  respMessageIDs.push(message.ID);
                  respMessages[message.ID] = message;

                  if (message?.mentions && Array.isArray(message?.mentions) && message.mentions?.length > 0) {
                    this.mentions.push(message.mentions);
                  }

                  if (message?.pxResults && Array.isArray(message?.pxResults) && message.pxResults?.length > 0) {
                    message.pxResults.forEach((item: any) => {
                      if (item?.mentions && Array.isArray(item?.mentions) && item.mentions?.length > 0) {
                        this.mentions.push(item.mentions);
                      }
                    });
                  }

                  if (
                    !isCustomViewConfigured &&
                    !['pzPostDetails', 'pzPrivatePostDetails'].includes(message.pyMessageViewReference)
                  )
                    isCustomViewConfigured = true;
                });
              }
            }

            if (isCustomViewConfigured) {
              await updateViewResources({ uiResources: response.data.uiResources });
            }

            if (!feedFilters) {
              feedFilters = [{ id: 'All', label: l11nService.getLocalizedText('All'), on: true, disabled: false }];

              if (response?.data?.data?.FeedFilters) {
                const feedFiltersList = response.data.data.FeedFilters?.pxResults;
                let allFilter = true;

                if (feedFiltersList && Array.isArray(feedFiltersList) && feedFiltersList?.length > 0) {
                  feedFiltersList.forEach((feedFilter) => {
                    const feedFilterItem = {
                      id: feedFilter.pyFeedSourceReference,
                      label: l11nService.getLocalizedText(feedFilter.pyLabel),
                      disabled: false,
                      on: feedFilter.pySelected === 'true'
                    };

                    if (!feedFilterItem.on) allFilter = false;
                    feedFilters.push(feedFilterItem);
                  });
                  const allFilterItem = feedFilters.find((feedItem) => feedItem.id === 'All');
                  if (allFilterItem) {
                    allFilterItem.on = allFilter;
                  }
                }
              }
            }
          }

          if (isLoadMore && pConnectObj.getValue('pulse.messageIDs')) {
            let messageIDs: any = pConnectObj.getValue('pulse.messageIDs');
            let messages = pConnectObj.getValue('pulse.messages');
            messageIDs = [...messageIDs, ...respMessageIDs];
            messages = { ...messages, ...respMessages };
            Object.values(messages).forEach((item: any) => {
              if (item?.mentions && Array.isArray(item?.mentions) && item.mentions?.length > 0) {
                this.mentions.push(item.mentions);
              }

              if (item?.pxResults && Array.isArray(item?.pxResults) && item.pxResults?.length > 0) {
                item.pxResults.forEach((ele: any) => {
                  if (ele?.mentions && Array.isArray(ele?.mentions) && ele.mentions?.length > 0) {
                    this.mentions.push(ele.mentions);
                  }
                });
              }
            });
            pConnectObj.updateState({
              pulse: {
                messages,
                messageIDs,
                nextPageFirstEntry: response?.data?.nextPageFirstEntry || '',
                pageLastEntry: response?.data?.pageLastEntry || '',
                hideAttachment: response?.data?.hideAttachment || false,
                postTypes,
                pulseContext
              }
            });
          } else {
            pConnectObj.updateState({
              pulse: {
                messages: respMessages,
                messageIDs: respMessageIDs,
                nextPageFirstEntry: response?.data?.nextPageFirstEntry || '',
                pageLastEntry: response?.data?.pageLastEntry || '',
                hideAttachment: response?.data?.hideAttachment || false,
                postTypes,
                pulseContext
              }
            });
          }
          // Removing duplicate mention objects from mentions array.
          const caseMentions =
            [...new Map(this.mentions.flat()?.map((item: any) => [item.mentionsID, item])).values()]
              ?.filter((item: any) => Object.hasOwn(item, 'mentionsType'))
              ?.filter((item: any) => !item?.mentionsType?.toLowerCase().includes('user')) || [];
          return { feedFilters, caseMentions };
        }
        pConnectObj.reportError(`getFeeds call failed ${response.status}`, response.data);
        return '';
      })
      .catch((error: any) => {
        if (!isRequestCanceled(error)) {
          pConnectObj.reportError(': Error occurred during ajax call at getFeeds API : ', error.response);
        }
      });
  };

  /**
   * Posts a message to the given context.
   * @function
   * @static
   * @example <caption>In this example, the API posts a message along with attachment to the given context.</caption>
   * PCore.getFeedUtils().postMessage('DATA-PORTAL $appName','test message', getPConnect(), [{"type":"File","category":"File","fileName":"attachment.png","ID":"459c"}], false, [])
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @example <caption>In this example, the API replies to a message along with attachment to the given context.</caption>
   * PCore.getFeedUtils().postMessage('PEGASOCIAL M-214007','test reply message',getPConnect(),[{"type":"File","category":"File","fileName":"attachment.png","ID":"459c"}], true,[])
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @example <caption>In this example, the API posts a message along with attachment to the given context that is visible only to the users listed in the recipients property.</caption>
   * PCore.getFeedUtils().postMessage('DATA-PORTAL $appName','test private message', getPConnect(),[], false, [{pyUserName:"venkat", pyUserIdentifier:"vel", pyPosition: "SWE"}])
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @param pulseContext    The name of the application context or case context for which the message must be posted.
   * @param message         The message that needs to be posted.
   * @param pConnectObj     The PConnect object of the component from where the API is being called.
   * @param attachmentIDs   The metadata of the attachments that need to be posted along with the message.
   * @param isReply         The flag that determines if you want to post a message or a reply to a message.The default value is false. Set isReply to true if you want to post a reply to a message. Set isReply to false if you want to post a message.
   * @param recipients      The list of users who can view the posted message.
   *                                          Note:
   *                                          1. The default value is [].
   *                                          2. If you want to post a private message, set recipients to an array of objects, where each object contains the pyUserName, pyUserIdentifier and pyPosition properties.
   *                                          3. If you want to post a public message, set recipients to an empty array.
   * @param       recipients.pyUserName          Name of the user who can view the message.
   * @param       recipients.pyUserIdentifier    Unique identifier of the user who can view the message.
   * @param       recipients.pyPosition          Position of the user who can view the message. Note: Send null if you don't have the position of the user.
   * @returns A Promise that resolves to an object.
   */
  postMessage = (
    pulseContext: string,
    message: string,
    pConnectObj: C11nEnv,
    attachmentIDs: any[] = [],
    isReply = false,
    recipients: Recipient[] = []
  ) => {
    const reqBody = {
      message,
      context: pulseContext,
      parentMessageId: isReply ? pulseContext : '',
      attachments: attachmentIDs,
      pyRecipients: recipients.length ? recipients : undefined
    };
    const payload =
      recipients.length === 0
        ? { body: JSON.stringify(reqBody), headers }
        : { body: JSON.stringify(reqBody), queryPayload: { type: 'PegaSocial-Post-Private' }, headers };
    return invokeRestApi(
      recipients.length ? POST_CUSTOM_MESSAGES : POST_FEED_MESSAGES,
      payload,
      pConnectObj.getContextName()
    )
      .then((response: any) => {
        if ((response.status === 201 || response.status === 200) && response.data) {
          const messageData = response?.data;
          if (isReply) {
            messageData.parentMessageID = pulseContext;
            const { pxResults, caseMentions } = this.buildMessageObject(
              messageData,
              isReply,
              pConnectObj,
              attachmentIDs.length > 0
            );
            pConnectObj.updateState({ pulse: { messages: { [pulseContext]: { pxResults } } } });
            return caseMentions;
          }
          const { messages, messageIDs, caseMentions } = this.buildMessageObject(
            messageData,
            isReply,
            pConnectObj,
            attachmentIDs.length > 0
          );
          pConnectObj.updateState({ pulse: { messages, messageIDs } });
          return caseMentions;
        }
        pConnectObj.reportError(`postMessage call failed with status ${response.status}`, response);
        return '';
      })
      .catch((error: any) => {
        pConnectObj.reportError(': Error occurred during ajax call at postMessage API : ', error.response);
        throw error.response;
      });
  };

  /**
   * Posts a specific type of message to the given context.
   *
   * @function
   * @static
   * @example <caption>In this example, the API posts a message whose type is associated with the PegaSocial-Post-Task classID.</caption>
   *
   * const payload = {
   *  "context": "DATA-PORTAL $TestABC",
   *  "content": {
   *    "label": "Sample label",
   *    "description": "Sample description"
   *  }
   * };
   *
   * PCore.getFeedUtils().postCustomMessage('PegaSocial-Post-Task', payload, getPConnect())
   * .then(response => {
   *    // Handle the response
   * }).catch(err => {
   *   // Handle errors
   * });
   *
   * @param {string} postTypeClassID    The unique identifier of the class associated with the type of the message to be posted.
   * @param {object} payload            The object containing the details of the message to be posted.
   * @param {string} payload.context    The name of the application context or case context for which the message must be posted.
   * @param {object} payload.content    A dynamically formed object based on the fields configured in the view of a post type.
   * @param {object} pConnectObj        The pConnect object of the component from where the API is being called.
   * @returns {Promise} A Promise that resolves to an object.
   */
  postCustomMessage = (postTypeClassID: string, payload: CustomMessageRequest, pConnectObj: C11nEnv) => {
    const POST_CREATED = 201;
    return invokeRestApi(
      POST_CUSTOM_MESSAGES,
      { body: payload, queryPayload: { type: postTypeClassID }, headers },
      pConnectObj.getContextName()
    )
      .then(async (response: CustomMessageResponse) => {
        if (response.status === POST_CREATED && response.data) {
          const messageData = response.data.data;
          const messageObject = {
            ID: messageData.ID,
            pzInsKey: messageData.ID,
            classID: messageData.classID,
            pyPostType: messageData.pyPostType,
            postedByUser: messageData.postedByUser,
            pyIsDeleteEnabled: messageData.pyIsDeleteEnabled,
            pyMessageViewReference: messageData.messageViewReference,
            pyFeed: {
              pyIconType: 'user',
              pyCommentContext: messageData.pyCommentContext,
              pyPostedOn: messageData.postedTime,
              pyFeedTitle: messageData.postedByUser.name,
              pyIconReference: messageData.postedByUser.ID
            },
            ...messageData.PostMessage
          };
          const viewResource = fetchViewResources(messageData.messageViewReference, pConnectObj, payload.context);

          if (!viewResource) {
            await updateViewResources({ uiResources: response.data.uiResources });
          }
          pConnectObj.updateState({
            pulse: {
              messages: { [messageObject.ID]: messageObject, ...pConnectObj.getValue('pulse.messages') },
              messageIDs: [messageObject.ID, ...pConnectObj.getValue('pulse.messageIDs')]
            }
          });
        }
        pConnectObj.reportError(`postCustomMessage call failed with status ${response.status}`, response);
        return '';
      })
      .catch((error: any) => {
        pConnectObj.reportError(': Error occurred during ajax call at postCustomMessage API : ', error.response);
        throw error.response;
      });
  };

  /**
   * Modifies a message associated with the given context.
   * @function
   * @static
   * @example <caption>In this example, the API edits the message whose messageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    messageID: 'PEGASOCIAL M-100',
   *    parentMessageID: 'PEGASOCIAL M-100',
   *    message: 'test message',
   *    isReply: false,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().editMessage(payload)
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @example <caption>In this example, the API edits the reply to the message whose messageID is PEGASOCIAL M-101 and parentmessageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    messageID: 'PEGASOCIAL M-101',
   *    parentMessageID: 'PEGASOCIAL M-100',
   *    message: 'test reply message',
   *    isReply: true,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().editMessage(payload)
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @example <caption>In this example, the API edits the message that is visible only to the users listed in the recipients property.</caption>
   *  const payload = {
   *    messageID: 'PEGASOCIAL M-100',
   *    parentMessageID: 'PEGASOCIAL M-100',
   *    message:'test message edited',
   *    isReply:true,
   *    pConnectObj: getPConnect(),
   *    recipients: [{pyUserName:"venkat", pyUserIdentifier:"vel", pyPosition: "SWE"}]
   *  }
   * PCore.getFeedUtils().editMessage(payload)
   * .then(() => {
   *   // success
   * }).catch(err => {
   *    // Error handling
   * });
   * @param param                 The object that contains the required data to edit a message.
   * @param param.messageID       The unique identifier(pzInsKey) of the message that needs to be edited.
   * @param param.parentMessageID The unique identifier(pzInsKey) of the parent message that needs to be edited.
   * @param param.message         The new message that replaces the existing message.
   * @param param.pConnectObj     The pConnect object of the component from where the API is being called.
   * @param param.isReply         The flag that determines if you want to post a message or a reply to a message.
   *                                          The default value is false. Set isReply to true if you want to edit a reply for a message.
   *                                          Set isReply to false if you want to edit a message.
   * @param param.recipients      The list of users who can view the edited message.
   *                                          Note:
   *                                          1. The default value is [].
   *                                          2. If you want to edit a private message, set recipients to an array of objects, where each object contains the pyUserName, pyUserIdentifier and pyPosition properties.
   *                                          3. If you want to edit a public message, set recipients to an empty array.
   * @param        param.recipients.pyUserName          Name of the user who can view the message.
   * @param        param.recipients.pyUserIdentifier    Unique identifier of the user who can view the message.
   * @param        param.recipients.pyPosition          Position of the user who can view the message.  Note: Send null if you don't have the position of the user.
   * @returns A Promise that resolves to an object.
   */
  editMessage = ({
    messageID,
    parentMessageID,
    message,
    pConnectObj,
    isReply = false,
    recipients = []
  }: {
    messageID: string;
    parentMessageID: string;
    message: string;
    pConnectObj: C11nEnv;
    isReply: boolean;
    recipients: Recipient[];
  }): Promise<unknown> => {
    const body = JSON.stringify({
      message,
      ...(recipients.length && { pyRecipients: recipients })
    });
    const queryPayload = { messageID };
    return invokeRestApi(EDIT_FEED_MESSAGE, { queryPayload, body, headers }, pConnectObj.getContextName())
      .then((response: any) => {
        if (response?.status === 200 && response?.data) {
          const messageData = response?.data;
          if (isReply) {
            messageData.parentMessageID = parentMessageID;
            const { pxResults, caseMentions: mentions } = this.buildMessageObject(
              messageData,
              isReply,
              pConnectObj,
              false
            );
            pConnectObj.updateState({ pulse: { messages: { [parentMessageID]: { pxResults } } } });
            return mentions;
          }
          const { messages, caseMentions } = this.buildMessageObject(messageData, isReply, pConnectObj, false);
          pConnectObj.updateState({ pulse: { messages } });
          return caseMentions;
        }
        pConnectObj.reportError(`editMessage call failed with status ${response.status}`, response);
        return '';
      })
      .catch((error: any) => {
        pConnectObj.reportError(': Error occurred during ajax call at editMessage API : ', error.response);
        throw error.response;
      });
  };

  /**
   * @private
   * @param messageData   The object that contains the newly added/edited message details.
   * @param isReply       The flag that determines if you want to post/edit a message or a reply to a message.
   * @param pConnectObj       The PConnect object of the component from where the API is being called.
   * @param hasAttachment The flag that determines if you have an attachment for a message.
   * @returns MessageObject The messageObject
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  buildMessageObject = (messageData: Message, isReply: boolean, pConnectObj: C11nEnv, hasAttachment = false): any => {
    const messageObject = {
      ID: messageData?.ID || '',
      classID: messageData?.classID || 'PegaSocial-Message',
      tags: messageData?.tags || [],
      pyHasAttachment: hasAttachment,
      pzInsKey: messageData?.ID || '',
      pyMessage: messageData?.message || '',
      mentions: messageData?.mentions || [],
      postedByUser: messageData?.postedByUser,
      pxCreateDateTime: messageData?.postedTime || '',
      pxUpdateDateTime: messageData?.updatedTime || '',
      parentMessageID: messageData?.parentMessageID || '',
      pyIsDeleteEnabled: messageData?.pyIsDeleteEnabled || false,
      pyIsEdited:
        typeof messageData?.isEdited === 'string' ? messageData?.isEdited === 'true' : messageData?.isEdited || false,
      pyFeed: {
        pyPostedOn: messageData?.postedTime || '',
        pyFeedTitle: messageData?.postedByUser?.name || '',
        pyIconType: 'user',
        pyCommentContext: messageData?.pyCommentContext || '.pzInsKey',
        pyIconReference: 'pi pi-case'
      },
      pyContext: '',
      pxIcon: 'globe',
      pyMessageViewReference: messageData?.messageViewReference || 'pzPostDetails',
      pyPostType: messageData?.pyPostType,
      pyRecipients: messageData?.pyRecipients?.length ? messageData.pyRecipients : undefined
    };
    const messages = pConnectObj.getValue('pulse.messages');
    if (isReply) {
      let { pxResults = [] }: { pxResults?: any[] } =
        Object.values(messages)?.find((item: any) => item?.ID === messageObject?.parentMessageID) || {};
      const index = pxResults?.findIndex((item: any) => item?.pzInsKey === messageObject?.ID);
      if (index !== -1) {
        const pxResultsCopy = [...pxResults];
        pxResultsCopy[index] = { ...pxResultsCopy[index], ...messageObject };
        pxResults = pxResultsCopy;
      } else {
        pxResults = [...pxResults, messageObject];
      }
      return {
        pxResults,
        messages: {},
        messageIDs: [],
        caseMentions: this.getCaseMentions(pxResults)
      };
    }
    const messageIDs = pConnectObj.getValue('pulse.messageIDs');

    if (messageIDs) {
      const updatedMessage = messageIDs.includes(messageObject.ID)
        ? { ...messages[messageObject.ID], ...messageObject }
        : messageObject;
      const updatedMessages = { ...messages, [messageObject.ID]: updatedMessage };
      const updatedMessageIDs = messageIDs.includes(messageObject.ID) ? messageIDs : [messageObject.ID, ...messageIDs];
      return {
        messages: updatedMessages,
        messageIDs: updatedMessageIDs,
        caseMentions: this.getCaseMentions(Object.values(updatedMessages))
      };
    }
  };

  /**
   * @private
   * @param array This param carries the list of mention objects
   * @returns The list of mention objects excluding the user mentions and duplicates
   */
  getCaseMentions = (array: Message[]): MentionItem[] => {
    const mentionsData = array
      ?.filter((item: any) => item?.mentions && Array.isArray(item?.mentions) && item.mentions?.length > 0)
      ?.map((item: any) => item.mentions)
      ?.concat(this.mentions);
    return (
      [...new Map(mentionsData.flat()?.map((item: any) => [item.mentionsID, item])).values()]
        ?.filter((item: any) => Object.hasOwn(item, 'mentionsType'))
        ?.filter((item: any) => !item?.mentionsType?.toLowerCase().includes('user')) || []
    );
  };

  /**
   * Likes or unlike a message.
   * @function
   * @static
   * @example <caption>In this example, the API likes the message whose messageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    pulseContext:'PEGAPROJMGMT-WORK TASK-100',
   *    likedBy: false,
   *    messageID:'PEGASOCIAL M-100',
   *    isReply: false,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().likeMessage(payload)
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @example <caption>In this example, the API dislikes the message whose messageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    pulseContext:'PEGAPROJMGMT-WORK TASK-100',
   *    likedBy: true,
   *    messageID:'PEGASOCIAL M-100',
   *    isReply: false,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().likeMessage(payload)
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @example <caption>In this example, the API likes the reply whose messageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    pulseContext:'PEGAPROJMGMT-WORK TASK-100',
   *    likedBy: false,
   *    messageID:'PEGASOCIAL M-100',
   *    isReply: true,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().likeMessage(payload)
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @example <caption>In this example, the API dislikes the reply whose messageID is PEGASOCIAL M-100.</caption>
   *  const payload = {
   *    pulseContext:'PEGAPROJMGMT-WORK TASK-100',
   *    likedBy: true,
   *    messageID:'PEGASOCIAL M-100',
   *    isReply: true,
   *    pConnectObj: getPConnect()
   *  }
   * PCore.getFeedUtils().likeMessage(payload)
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @param param               The object that contains all the required data to like or unlike a specific message.
   * @param param.pulseContext  The name of the application context or case context for which the feed must be fetched.
   * @param param.likedBy       The value that determines whether to like or unlike the message.
   * @param param.messageID     The ID(pzInsKey) of the message that needs to be liked or unlike.
   * @param param.isReply       The flag that determines whether the number of likes must be obtained for a message or a reply to a message.
   * @param param.pConnectObj   The PConnect object of the component from where the API is being called.
   */
  likeMessage = ({
    pulseContext,
    likedBy: unLiked,
    messageID,
    isReply,
    pConnectObj
  }: {
    pulseContext: string;
    likedBy: boolean;
    messageID: string;
    isReply: boolean;
    pConnectObj: C11nEnv;
    // eslint-disable-next-line sonarjs/cognitive-complexity
  }) => {
    const routeKey = unLiked ? UNLIKE_FEED_MESSAGES : LIKE_FEED_MESSAGES;
    const body = JSON.stringify({
      ContextClass: pulseContext
    });
    const queryPayload = {
      pulseContext
    };
    invokeRestApi(routeKey, { body, queryPayload, headers }, pConnectObj.getContextName())
      .then((response: any) => {
        if (response.status === 200) {
          if (isReply) {
            const msg = pConnectObj.getValue(`pulse.messages.${messageID}`);
            const pxResults =
              msg?.pxResults &&
              Array.isArray(msg?.pxResults) &&
              msg.pxResults?.length > 0 &&
              msg.pxResults?.map((reply: any) => {
                reply = { ...reply };

                if (reply.pzInsKey === pulseContext) {
                  let updatedLikeCount = 0;
                  let updatedLikedFlag = false;
                  const likeCount = reply.pyLikes ? reply?.pyLikes?.pxLikeCount : 0;
                  if (unLiked) {
                    updatedLikeCount = likeCount - 1;
                    updatedLikedFlag = false;
                  } else {
                    updatedLikeCount = likeCount + 1;
                    updatedLikedFlag = true;
                  }
                  reply.pyLikes = {
                    pxLikeCount: updatedLikeCount,
                    pxIsLiked: updatedLikedFlag
                  };
                }
                return reply;
              });

            pConnectObj.updateState({
              pulse: {
                messages: {
                  [messageID]: {
                    pxResults
                  }
                }
              }
            });
          } else {
            const msg = { ...pConnectObj.getValue(`pulse.messages.${messageID}`) };
            const likeCount = msg.pyLikes ? msg.pyLikes.pxLikeCount : 0;
            let updatedLikeCount = 0;
            let updatedLikedFlag = false;

            if (unLiked) {
              updatedLikeCount = likeCount - 1;
              updatedLikedFlag = false;
            } else {
              updatedLikeCount = likeCount + 1;
              updatedLikedFlag = true;
            }

            msg.pyLikes = {
              pxLikeCount: updatedLikeCount,
              pxIsLiked: updatedLikedFlag
            };

            pConnectObj.updateState({
              pulse: {
                messages: {
                  [pulseContext]: msg
                }
              }
            });
          }
        } else {
          pConnectObj.reportError(`likeMessage call failed with status ${response.status}`, response);
        }
      })
      .catch((error: any) => {
        pConnectObj.reportError(': Error occurred during ajax call at likeMessage API : ', error.response.data);
      });
  };

  /**
   * Deletes a message from a given context.
   * @function
   * @static
   * @example <caption>In this example, the API removes the reply whose ID is PEGASOCIAL M-101 from a message whose ID is PEGASOCIAL M-100.</caption>
   * PCore.getFeedUtils().deleteMessage('PEGASOCIAL M-100', true, 'PEGASOCIAL M-101', getPConnect())
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @example <caption>In this example, the API removes the message whose ID is PEGASOCIAL M-100</caption>
   * PCore.getFeedUtils().deleteMessage('PEGASOCIAL M-100', false, '', getPConnect())
   * .then(() => {
   *    // success
   * }).catch(err => {
   *   // errors
   * });
   * @param messageID  The ID of the message that needs to be deleted. Note: Ensure that you provide the pzInsKey value of the messageID.
   * @param isReply    The flag that determines if you want to delete a message or a reply to a message.
   *                   Note: The default value is false.
   *                         Set isReply to true if you want to delete a reply to a message.
   *                         Set isReply to false if you want to delete a message.
   * @param replyID    The ID of the reply that needs to be deleted.
   *                   Note:This parameter is required if isReply is set to true.
   *                        If isReply is set to false, pass an empty string as the replyID.
   * @param pConnectObj    The PConnect object of the component from where the API is being called.
   */
  deleteMessage = (messageID: string, isReply: boolean, replyID: string, pConnectObj: C11nEnv) => {
    let messageKey = messageID;
    if (isReply) {
      messageKey = replyID;
    }
    const queryPayload = {
      messageID: messageKey
    };

    invokeRestApi(DELETE_FEED_MESSAGE, { queryPayload }, pConnectObj.getContextName())
      .then((response: any) => {
        if (response.status === 200) {
          if (isReply) {
            const msg = pConnectObj.getValue(`pulse.messages.${messageID}`);
            const pxResults =
              msg?.pxResults &&
              Array.isArray(msg?.pxResults) &&
              msg.pxResults?.length > 0 &&
              msg.pxResults?.filter((reply: any) => reply.pzInsKey !== replyID);

            pConnectObj.updateState({
              pulse: {
                messages: {
                  [messageID]: {
                    pxResults
                  }
                }
              }
            });
          } else {
            const msgIDs = pConnectObj.getValue('pulse.messageIDs');
            const newMsgIDs =
              msgIDs && Array.isArray(msgIDs) && msgIDs?.length > 0 && msgIDs?.filter((msgID) => msgID !== messageID);

            const msgs = { ...pConnectObj.getValue('pulse.messages') };
            delete msgs[messageID];

            pConnectObj.updateState({
              pulse: {
                messageIDs: newMsgIDs,
                messages: msgs
              }
            });
          }
        } else {
          pConnectObj.reportError(`deleteMessage call failed with status ${response.status}`, response);
        }
      })
      .catch((error: any) => {
        pConnectObj.reportError(': Error occurred during ajax call at deleteMessage API : ', error.response);
      });
  };

  /**
   * Obtains the list of users who liked the message.
   * @function
   * @static
   * @example <caption>In this example, the API obtains the list of users who liked the message with messageID PEGASOCIAL M-100</caption>
   * PCore.getFeedUtils().getLikedUsers(PEGASOCIAL M-100, getPConnect())
   * .then(response => {
   *    // response array
   * }).catch(err => {
   *   // errors
   * });
   *
   * @param messageID         The ID of the message that is liked by the users. Note: Ensure that you provide the pzInsKey value of the messageID.
   * @param pConnectObj           The PConnect object of the component from where the API is being called.
   * @returns A Promise that resolves to an object.
   */
  getLikedUsers = (messageID: string, pConnectObj: C11nEnv): Promise<object> => {
    const queryPayload = {
      messageID
    };
    return invokeRestApi(GET_FEED_MESSAGE_LIKES, { queryPayload }, pConnectObj.getContextName());
  };

  /**
   * Obtains the list of options for the selected object that can be mentioned in a Pulse post.
   * @function
   * @static
   * @example <caption>In this example, the API obtains the list of cases that can be mentioned in a Pulse post.</caption>
   * PCore.getFeedUtils().getMentionSuggestions({searchFor:"case", mentionsType = 'Cases', listSize:5}, getPConnect())
   * .then(mentionsResponse => {
   *    // mentionsResponse array
   * }).catch(err => {
   *   // errors
   * });
   * @param mentionProps     The object that contains the search parameters for obtaining the list of options for the selected object.
   * @param pConnectObj      The PConnect object of the component from where the API is being called.
   * @returns A Promise that resolves to an object.
   */
  getMentionSuggestions = (
    mentionProps: {
      pulseContext?: string;
      searchFor: string;
      mentionsType: string;
      listSize?: number;
    },
    pConnectObj: C11nEnv
  ): Promise<object> => {
    const { pulseContext, searchFor, mentionsType, listSize = 5 } = mentionProps;
    const queryPayload = {
      pulseContext,
      searchFor,
      mentionsType,
      listSize
    };

    for (let i = 0; i < this.mentionsTagsCancelTokenSource.length; i += 1) {
      this.mentionsTagsCancelTokenSource[i].cancel();
    }
    const newCancelTokenSource = getCancelTokenSource();
    this.mentionsTagsCancelTokenSource.push(newCancelTokenSource);
    return invokeRestApi(
      GET_MENTION_SUGGESTIONS,
      {
        queryPayload,
        cancelTokenSource: newCancelTokenSource
      },
      pConnectObj.getContextName()
    )
      .then((response: any) => {
        this.mentionsTagsCancelTokenSource = [];
        if (response.status === 200 && response.data) return response?.data;
        pConnectObj.reportError(`Get mention suggestions call failed ${response.status}`, response.data);
        return [];
      })
      .catch((err: any) => {
        if (!isRequestCanceled(err)) {
          pConnectObj.reportError(
            ': Error occurred during ajax call at getMentionSuggestions API : ',
            err.response.data
          );
        }
      });
  };

  /**
   * Obtains the list of available types of objects that can be mentioned in a Pulse post.
   * @function
   * @static
   * @example <caption>In this example, the API obtains the list of available types of objects that can be mentioned in a Pulse post.</caption>
   * PCore.getFeedUtils().getMentionTypes(getPConnect())
   * .then(response => {
   *    // response array
   * }).catch(err => {
   *   // errors
   * });
   * @param pConnectObj      The PConnect object of the component from where the API is being called.
   * @returns A Promise that resolves to an object.
   */
  getMentionTypes = (pConnectObj: C11nEnv): Promise<object> => {
    return invokeRestApi(GET_MENTION_TYPES, {}, pConnectObj.getContextName());
  };

  /**
   * Retrieves the view for a specified post type.
   * @function
   * @static
   * @example <caption>In this example, the API retrieves the view whose type is associated with the PegaSocial-Post-Task classID.</caption>
   * PCore.getFeedUtils().getPostTypeView('PegaSocial-Post-Task', getPConnect())
   * .then(view => {
   *    // Handle the view
   * }).catch(err => {
   *   // Handle errors
   * });
   * @param postTypeClassID   The unique identifier of the class associated with the type of the message to be posted.
   * @param pConnectObj       The pConnect object of the component from where the API is being called.
   * @returns   A Promise that resolves to an object.
   */
  getPostTypeView = (postTypeClassID: string, pConnectObj: C11nEnv): Promise<object> => {
    const queryPayload = {
      type: postTypeClassID
    };
    return invokeRestApi(GET_VIEW_FOR_POSTTYPE, { queryPayload }, pConnectObj.getContextName());
  };

  /**
   * Obtains the options suggested for selecting a tag for a given context.
   * @function
   * @static
   * @example <caption>In this example, the API obtains the options suggested for selecting a tag for the given context.</caption>
   * PCore.getFeedUtils().getTagSuggestions({searchFor:"test", listSize:5}, getPConnect())
   * .then(tagsResponse => {
   *    // tagsResponse array
   * }).catch(err => {
   *   // errors
   * });
   * @param tagProps      The object that contains the search parameters for obtaining the list of tags.
   * @param pConnectObj       The PConnect object of the component from where the API is being called.
   * @returns A Promise that resolves to an object.
   */
  getTagSuggestions = (
    tagProps: {
      searchFor: string;
      listSize?: number;
    },
    pConnectObj: C11nEnv
  ): Promise<string[]> => {
    const { searchFor, listSize = 5 } = tagProps;
    const queryPayload = {
      searchFor,
      listSize
    };

    for (let i = 0; i < this.mentionsTagsCancelTokenSource.length; i += 1) {
      this.mentionsTagsCancelTokenSource[i].cancel();
    }
    const newCancelTokenSource = getCancelTokenSource();
    this.mentionsTagsCancelTokenSource.push(newCancelTokenSource);
    return invokeRestApi(
      GET_TAG_SUGGESTIONS,
      {
        queryPayload,
        cancelTokenSource: newCancelTokenSource
      },
      pConnectObj.getContextName()
    )
      .then((response: any) => {
        this.mentionsTagsCancelTokenSource = [];
        let tagSuggestions = [];
        if (response.status === 200 && response?.data) {
          tagSuggestions =
            response?.data?.tags &&
            Array.isArray(response?.data?.tags) &&
            response.data.tags.length > 0 &&
            response.data.tags?.map((tag: any) => `#${tag}`);
        } else {
          pConnectObj.reportError(`Get tag suggestions call failed ${response.status}`, response.data);
        }
        return tagSuggestions;
      })
      .catch((err: any) => {
        if (!isRequestCanceled(err)) {
          pConnectObj.reportError(': Error occurred during ajax call at getTagSuggestions API : ', err.response.data);
        }
      });
  };
}

export default new FeedUtils();
